/*
 * Copyright (c) 2023. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box, Container } from '@mui/material';
import { type APIPermissions, objectEntries, type RoutePath } from '@poweralpha/common';
import { useMemo } from 'react';
import { Navigate, Route as ReactRoute, Routes as ReactRoutes } from 'react-router-dom';
import { usePermissions } from '../../auth';
import { PageErrorCard } from '../containers';
import usePANavigate from './usePANavigate';

interface Props {
  pathElementMap: Partial<Record<RoutePath, React.ReactNode>>;
  permissionFilter?: (
    key: RoutePath,
    permissions: Array<APIPermissions> | undefined,
  ) => boolean | undefined;
  reroutes?: Record<string, string>;
}

export function Routes({ pathElementMap, permissionFilter, reroutes }: Readonly<Props>) {
  const permissions = usePermissions();
  const { location } = usePANavigate();

  const filteredRoutes = useMemo(() => {
    const entries = objectEntries(pathElementMap);
    if (!permissionFilter) return entries;
    return entries.map(([path, element]) => {
      if (permissionFilter(path, permissions)) {
        return [path, element] satisfies [RoutePath, React.ReactNode];
      }

      return [
        path,
        <Container key={path} maxWidth="md">
          <PageErrorCard />
        </Container>,
      ] satisfies [RoutePath, React.ReactNode];
    });
  }, [pathElementMap, permissionFilter, permissions]);

  return useMemo(
    () => (
      <Box sx={{ height: '100%', minHeight: 0, minWidth: 0, width: '100%' }}>
        <ReactRoutes>
          {filteredRoutes.map(([path, element]) => (
            <ReactRoute key={path} path={path} element={element} />
          ))}
          {Object.entries(reroutes ?? {}).map(([from, to]) => (
            <ReactRoute
              key={from}
              path={from}
              element={<Navigate to={`${to}${location.search}`} replace />}
            />
          ))}
          <ReactRoute
            path="*"
            element={
              <Container maxWidth="md">
                <PageErrorCard error="404 Not Found" />
              </Container>
            }
          />
        </ReactRoutes>
      </Box>
    ),
    [filteredRoutes, location.search, reroutes],
  );
}
